<template>
  <div v-if="customer" class="py-6">
    <div class="flex justify-between items-center mb-6">
      <div class="">
        <h4 class="text-lg mr-4 leading-none font-semibold opacity-80">
          {{ $t('general.reservations') }}
        </h4>
      </div>

      <div class="flex-shrink-0 ml-12">
        <o-panel-link
          v-if="!customer.isArchived"
          :props="{ presets: { customer } }"
          tag="o-button"
          path="/appointment/create"
          flat
          variant="primary"
        >
          {{ $t('reservation.new') }}
        </o-panel-link>
      </div>
    </div>
    <!--<div v-if="!customer.isArchived" class="flex items-center space-x-3 mb-10">
      <o-statistic
        :title="$t('general.appointments')"
        :value="customer.stats.reservationCount"
        icon="reservation"
        class="w-1/4 max-w-50"
        variant="primary"
      />
      <o-statistic
        :title="$t('general.revenue')"
        :value="$auth.info.location.currencySymbol + customer.stats.reservationRevenue.toFixed(2)"
        icon="currency"
        class="w-1/4 max-w-50"
        variant="primary"
      />
      <o-statistic
        v-if="customer.stats.reservationRevenue && customer.stats.reservationCount"
        :title="$t('customer.averageValue')"
        :value="$auth.info.location.currencySymbol + (customer.stats.reservationRevenue / customer.stats.reservationCount).toFixed(2)"
        icon="chart"
        class="w-1/4 max-w-50"
        variant="primary"
      />
    </div>-->

    <o-pager
      v-if="$features.get('waitlist') && pagers.waitlist.items"
      :header="$t('general.waitlist')"
      :button="$t('general.loadMore')"
      :empty="$t('components.customer.profile.reservations.noWaitlistRequests')"
      header-class="flex justify-between items-center mb-4 border-b pb-2 dark:border-gray-800"
      class="mb-16"
      :list="pagers.waitlist"
    >
      <template #default="{data}">
        <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-4">
          <waitlist
            v-for="waitlist in data"
            :key="waitlist.idWaitlist"
            :waitlist="waitlist"
            class="w-full block px-3 md:px-4 py-2 hover:bg-blue-100 dark:hover:bg-blue-950 transition-colors duration-200"
          />
        </div>
      </template>
    </o-pager>

    <o-pager
      :header="$t('components.customer.profile.reservations.upcoming')"
      :button="$t('general.loadMore')"
      :empty="$t('reservation.noUpcoming')"
      header-class="flex justify-between items-center mb-4 border-b pb-2 dark:border-gray-800"
      class="mb-16"
      :list="pagers.upcoming"
    >
      <template #default="{data}">
        <div v-for="(group, key) in split(data)" :key="key" class="mb-8">
          <p class="text-xs mb-3 tracking-wider opacity-70 uppercase">
            {{ key }}
          </p>
          <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-4">
            <template v-for="reservation in group" :key="keyForReservation(reservation)">
              <reservation

                :data="reservation"
                class="w-full block px-3 md:px-4 py-2 hover:bg-blue-100 dark:hover:bg-blue-950 transition-colors duration-200 rounded-xl"
              />

              <hr class="w-full border-gray-100 dark:border-gray-800 last:hidden">
            </template>
          </div>
        </div>
      </template>
    </o-pager>

    <o-pager
      :header="$t('components.customer.profile.reservations.past')"
      :button="$t('general.loadMore')"
      :empty="$t('components.customer.profile.reservations.noPast')"
      header-class="flex justify-between items-center mb-4 border-b pb-4 dark:border-gray-800"
      :list="pagers.past"
    >
      <template #default="{data}">
        <div v-for="(group, key) in split(data)" :key="key" class="mb-8">
          <p class="text-xs mb-3 tracking-wider opacity-70 uppercase">
            {{ key }}
          </p>
          <div class="flex flex-col overflow-hidden p-2 md:p-2 items-center h-full bg-white dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-4">
            <template v-for="reservation in group" :key="keyForReservation(reservation)">
              <reservation

                :data="reservation"
                class="w-full block px-3 md:px-4 py-2 hover:bg-blue-100 dark:hover:bg-blue-950 transition-colors duration-200 rounded-xl"
              />

              <hr class="w-full border-gray-100 dark:border-gray-800 last:hidden">
            </template>
          </div>
        </div>
      </template>
    </o-pager>
  </div>
</template>

<script setup lang="ts">
import { EndlessListPager } from '@/_api/lib/pager'

import Reservation from './reservation.vue'
import Waitlist from './waitlist.vue'

import { OStatistic, OPager } from '@/components/elements'

interface Props {
  customer?: Record<string, any> | null
}

const props = withDefaults(defineProps<Props>(), {
  customer: null,
})

const { $bus, $date, $api } = useNuxtApp()

const customerId = computed(() => props.customer?.idCustomer || props.customer?.id)

const pagers = reactive({
  upcoming: new EndlessListPager((page: number) => $api.customer.upcomingReservations(String(customerId.value), { page })),
  past: new EndlessListPager((page: number) => $api.customer.pastReservations(String(customerId.value), { page })),
  waitlist: new EndlessListPager((page: number) => $api.customer.waitlists(String(customerId.value), { page })),
})

const keyForReservation = (reservation: any) => {
  let key = reservation.idReservation + '-' + reservation.date.integer
  if (reservation.reservationCustomer) {
    key = key + '-' + reservation.reservationCustomer.idReservationCustomer
  }
  return key
}

const split = (data: any[]) => {
  return data.reduce((acc: Record<string, any[]>, cur: any) => {
    const date = ($date as any).convert(cur.date.value, undefined, 'MMMM yyyy')
    acc[date] = [...acc[date] || [], cur]
    return acc
  }, {})
}

// created
const uid = getCurrentInstance()?.uid
$bus.on.all('Reservation', uid, (reservation: any) => {
  const idCustomer = props.customer!.idCustomer
  const current = (reservation.customer && reservation.customer.idCustomer === idCustomer) || reservation.groupCustomers?.some(({ customer }: any) => customer.idCustomer === idCustomer)

  if (current) {
    pagers.upcoming.reload()
    pagers.past.reload()
  }
})

$bus.on.all('Waitlist', uid, (waitlist: any) => {
  const idCustomer = props.customer!.idCustomer
  const current = (waitlist.customer && waitlist.customer.idCustomer === idCustomer)

  if (current) {
    pagers.waitlist.reload()
  }
})

onMounted(() => {
  if (customerId.value) {
    pagers.upcoming.load()
    pagers.past.load()
    pagers.waitlist.load()
  }
})

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
